import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Check, Loader2, Users, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCheckIn } from '../hooks/useCheckIn';
import { CheckInModal } from './CheckInModal';
import { cn } from '@/lib/utils';

interface CheckInButtonProps {
  displayId: number;
  displayName?: string;
  className?: string;
  variant?: 'default' | 'compact' | 'full';
  showStats?: boolean;
  onRequireAuth?: () => void;
}

export const CheckInButton: React.FC<CheckInButtonProps> = ({
  displayId,
  displayName,
  className,
  variant = 'default',
  showStats = true,
  onRequireAuth,
}) => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const {
    hasCheckedIn,
    todayCheckedIn,
    totalCheckIns,
    uniqueVisitors,
    userCheckIns,
    loading,
    canCheckIn,
    checkIn,
  } = useCheckIn(displayId);

  const handleClick = async () => {
    if (!user) {
      onRequireAuth?.();
      return;
    }

    if (todayCheckedIn) {
      return; // Already checked in today
    }

    // For compact variant, check in directly
    if (variant === 'compact') {
      await checkIn();
      return;
    }

    // Otherwise, show modal
    setShowModal(true);
  };

  const handleModalCheckIn = async (data?: any) => {
    const result = await checkIn(data);
    if (result.success) {
      setShowModal(false);
    }
    return result;
  };

  if (variant === 'compact') {
    return (
      <Button
        onClick={handleClick}
        disabled={loading || (user && todayCheckedIn)}
        size="sm"
        variant={todayCheckedIn ? 'secondary' : 'default'}
        className={cn(
          'gap-1.5 text-sm',
          todayCheckedIn && 'bg-green-100 text-green-700 hover:bg-green-200',
          className
        )}
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : todayCheckedIn ? (
          <Check className="w-3 h-3" />
        ) : (
          <MapPin className="w-3 h-3" />
        )}
        {todayCheckedIn ? 'Checked In' : 'Check In'}
        {showStats && totalCheckIns > 0 && (
          <Badge variant="secondary" className="ml-1 px-1 py-0 text-xs">
            {totalCheckIns}
          </Badge>
        )}
      </Button>
    );
  }

  if (variant === 'full') {
    return (
      <div className={cn('space-y-3', className)}>
        <Button
          onClick={handleClick}
          disabled={loading || (user && todayCheckedIn)}
          size="lg"
          variant={todayCheckedIn ? 'secondary' : 'default'}
          className={cn(
            'w-full gap-2',
            todayCheckedIn && 'bg-green-100 text-green-700 hover:bg-green-200'
          )}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : todayCheckedIn ? (
            <Check className="w-4 h-4" />
          ) : (
            <MapPin className="w-4 h-4" />
          )}
          {todayCheckedIn ? 'Checked In Today' : 'Check In Here'}
        </Button>

        {showStats && (
          <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
            {totalCheckIns > 0 && (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{totalCheckIns} check-ins</span>
              </div>
            )}
            {uniqueVisitors > 0 && (
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                <span>{uniqueVisitors} visitors</span>
              </div>
            )}
            {hasCheckedIn && userCheckIns > 1 && (
              <div className="text-blue-600">
                You've visited {userCheckIns} times
              </div>
            )}
          </div>
        )}

        {showModal && (
          <CheckInModal
            displayId={displayId}
            displayName={displayName}
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onCheckIn={handleModalCheckIn}
          />
        )}
      </div>
    );
  }

  // Default variant
  return (
    <>
      <Button
        onClick={handleClick}
        disabled={loading || (user && todayCheckedIn)}
        variant={todayCheckedIn ? 'secondary' : 'default'}
        className={cn(
          'gap-2',
          todayCheckedIn && 'bg-green-100 text-green-700 hover:bg-green-200',
          className
        )}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : todayCheckedIn ? (
          <Check className="w-4 h-4" />
        ) : (
          <MapPin className="w-4 h-4" />
        )}
        <span>{todayCheckedIn ? 'Checked In' : 'Check In'}</span>
        {showStats && totalCheckIns > 0 && (
          <Badge variant="secondary" className="ml-1">
            {totalCheckIns}
          </Badge>
        )}
      </Button>

      {showModal && (
        <CheckInModal
          displayId={displayId}
          displayName={displayName}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onCheckIn={handleModalCheckIn}
        />
      )}
    </>
  );
};

// Stats-only component for displaying check-in stats
export const CheckInStats: React.FC<{
  totalCheckIns: number;
  uniqueVisitors: number;
  className?: string;
}> = ({ totalCheckIns, uniqueVisitors, className }) => {
  if (totalCheckIns === 0 && uniqueVisitors === 0) {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-3 text-sm text-gray-600', className)}>
      {totalCheckIns > 0 && (
        <div className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          <span>{totalCheckIns} check-ins</span>
        </div>
      )}
      {uniqueVisitors > 0 && (
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          <span>{uniqueVisitors} visitors</span>
        </div>
      )}
    </div>
  );
};